import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { OrderDetailResponse, SelectionPart, SelectionStatus } from './useGetOrderDetail';
import { PartOptionsResponse, PartOption } from './useGetPartOptions';
import { invalidateOrder } from './invalidateOrder';

interface UpdatePartOptionInput {
    orderPartId: string;
    selectedOption: PartOption;
    source: 'flagged' | 'order';
    orderId: string;
}

const updatePartOption = async (sessionId: string, input: UpdatePartOptionInput) => {
    const { data } = await axios.post(`${API_URL}/order/part/update`,
        {
            orderPartId: input.orderPartId,
            selectedOptionId: input.selectedOption._id,
            source: input.source,
        },
        {
            headers: {
                Authorization: sessionId
            }
        }
    );
    return data;
};

export const useUpdatePartOption = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: UpdatePartOptionInput) => updatePartOption(sessionContext?.sessionId ?? '', input),
        onMutate: (variables) => {
            if (variables.selectedOption.source === 'ccc-sp') return;
            queryClient.setQueryData(
                ['/order/detail', variables.orderId],
                (old: OrderDetailResponse | undefined): OrderDetailResponse | undefined => {
                    if (!old?.selection) return old;

                    const listPrice = variables.selectedOption.adjustedListPrice ?? variables.selectedOption.listPrice;

                    // Update the selection tabs
                    const updatedSelection = old.selection.map((tab) => ({
                        ...tab,
                        parts: tab.parts.map((part: SelectionPart) =>
                            part.partId === variables.orderPartId
                                ? {
                                    ...part,
                                    selectionStatus: 'manual' as SelectionStatus,
                                    selectedOptionId: variables.selectedOption._id,
                                    type: variables.selectedOption.type,
                                    vendor: variables.selectedOption.supplierText,
                                    delivery: variables.selectedOption.deliveryString,
                                    deliveryHours: variables.selectedOption.delivery,
                                    listPriceAdjusted: variables.selectedOption.listPriceAdjusted,
                                    quantity: variables.selectedOption.qty,
                                    listPrice: listPrice,
                                    buyPrice: variables.selectedOption.buyPrice,
                                    grossMargin: (listPrice - variables.selectedOption.buyPrice) / listPrice,
                                    manuallyAdjusted: true,
                                    flag: false,
                                    flagReason: undefined
                                }
                                : part
                        )
                    }));

                    return {
                        ...old,
                        selection: updatedSelection
                    };
                }
            );
            queryClient.setQueryData(
                ['/order/part', variables.orderPartId],
                (old: PartOptionsResponse | undefined): PartOptionsResponse | undefined => {
                    if (!old) return old;

                    return {
                        ...old,
                        flagReason: null,
                        options: old.options.map((option: PartOption) => ({
                            ...option,
                            selected: option._id === variables.selectedOption._id,
                        }))
                    };
                }
            );
        },
        onSuccess: (_, variables) => {
            if (variables.selectedOption.source === 'ccc-sp') return;
            void invalidateOrder(queryClient, variables.orderId);
            void queryClient.invalidateQueries({ queryKey: ['/order/detail', variables.orderId] });
            void queryClient.invalidateQueries({ queryKey: ['/order/part', variables.orderPartId] });
        },
    });
};
